/**
 * Gap analysis — the former standalone /gaps page, folded into Learn as a
 * section beside "Report an issue".
 *
 * Both are ways to feed real signal back into the standards: a reported issue
 * (or a CI failure) on one side, the coverage gaps in the captured requirements
 * on the other. Runs the AI gap analysis and lists what's missing by priority.
 * Analysis is tenant-scoped server-side (TENANT# rides the auth token), so no
 * slug is needed here — the CaptureHost is created with a null slug purely to
 * reach `analyzeGaps`.
 */

import { useState, useMemo } from 'react';
import { Sparkles, AlertCircle, ArrowRight } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { cn } from '@/lib/utils';
import { createSystemdoxCaptureHost, type CaptureGap } from '@/capture/captureHost';
import { useCapabilities } from '@/hooks/useCapabilities';

const PRIORITY_STYLE: Record<CaptureGap['priority'], string> = {
  high: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  medium: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  low: 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400',
};

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 } as const;

export function GapAnalysisSection() {
  const host = useMemo(() => createSystemdoxCaptureHost({ tenantSlug: null }), []);
  const { isDisabled } = useCapabilities();
  const [state, setState] = useState<'idle' | 'analyzing' | 'done' | 'error'>('idle');
  const [gaps, setGaps] = useState<CaptureGap[]>([]);
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');

  // Gap analysis exists to find missing REQUIREMENTS — a workspace that
  // deliberately disabled the requirements capability has nothing for it to
  // nag about, so the whole section stands down (guided-repo-setup Phase D).
  if (isDisabled('requirements')) return null;

  async function analyze() {
    setState('analyzing');
    setError('');
    try {
      const result = await host.analyzeGaps();
      setGaps([...result.gaps].sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]));
      setSummary(result.summary);
      setState('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analysis failed');
      setState('error');
    }
  }

  return (
    <div className="mb-8 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/10 p-6">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-teal-600" />
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Gap analysis</h3>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
        Find what's missing across your captured requirements — edge cases, non-functional needs, untested criteria.
      </p>

      {state === 'idle' && (
        <button
          onClick={analyze}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
        >
          <Sparkles className="w-4 h-4" /> Analyze gaps
        </button>
      )}

      {state === 'analyzing' && (
        <LoadingState size="md" label="Analyzing your requirements…" />
      )}

      {state === 'error' && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {state === 'done' && (
        <div className="space-y-3">
          {summary && (
            <div className="rounded-md border border-teal-200 dark:border-teal-800 bg-white dark:bg-slate-900 p-3 text-sm text-slate-700 dark:text-slate-300">
              {summary}
            </div>
          )}
          {gaps.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No significant gaps found.</p>
          ) : (
            <ul className="space-y-2">
              {gaps.map((gap, i) => (
                <li key={i} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={cn('text-[10px] font-medium uppercase px-1.5 py-0.5 rounded', PRIORITY_STYLE[gap.priority])}>
                      {gap.priority}
                    </span>
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{gap.area}</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{gap.description}</p>
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={analyze}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Re-analyze <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
